import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import endpoints from '@constants/configs/endpoints';
import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@modules/common-utils-services/axiosUtil';
import { startSpinner, stopSpinner } from '@actions/common/actionCreators';
import { ContentIds, ISubmitHandler } from '../constants/media.interface';
import { HTML_CODE, MEDIA_CODE, TRANSCRIPTION_CODE } from '../constants/media.constants';

interface IUseMediaFormHandlerProps {
    defaultValues?: {
        interactionId?: number;
    };
}

const useMediaFormHandler = ({defaultValues}: IUseMediaFormHandlerProps) => {
    const dispatch = useDispatch();
    const [ mediaNotFoundError, setMediaNotFoundError ] = useState('');
    const mediaFormSchema = z.object({
        interactionId: z.number().min(1, { message: 'cap.mediaAccess.error.interactionIdRequired' })
    });

    const formData = useForm({
        defaultValues: defaultValues || {
            interactionId: 0,
        },
        resolver: zodResolver(mediaFormSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    const onSubmitHandler = async ({ setContentData, contactId, fromAccountActivity = false }: ISubmitHandler) => {
        const url = endpoints.MEDIA_ACCESS.processReplacements({ contactId });
        dispatch(startSpinner('cap.mediaAccess.loading'));
        try {
            const response = await api.get(url);
            const mediaFiles = response?.data?.data?.content || [];
            const media = {};
            const urls = { media: '', html: '', transcription: '' };
            for (const item of mediaFiles) {
                const { mediaType, isMediaOrHtml, url, data } = item;
                if (mediaType === MEDIA_CODE && isMediaOrHtml) {
                    media['mediaUrl'] = url;
                    urls['media'] = url;
                } else if (mediaType === HTML_CODE && (!!data || isMediaOrHtml)) {
                    media['interaction'] = data;
                    urls['html'] = url;
                } else if (mediaType === TRANSCRIPTION_CODE && (!!data || isMediaOrHtml)) {
                    media['transcription'] = data;
                    urls['transcription'] = url;
                }
            };
            setContentData({
                id: ContentIds.DISPLAY_MEDIA,
                props: {
                    interactionId: contactId,
                    mediaData: media,
                    fileUrls: urls,
                    fromAccountActivity,
                }
            });
        } catch (error) {
            setMediaNotFoundError(fromAccountActivity ? 'cap.mediaAccess.unableToLocate.errorWithParams' : 'cap.mediaAccess.unableToLocate.error');
        } finally {
            dispatch(stopSpinner());
        }
    };

    return {
        ...formData,
        onSubmitHandler,
        mediaNotFoundError,
        setMediaNotFoundError,
    };
};

export default useMediaFormHandler;